import { styled } from '@porsche-code/kado-styled-system/jsx';
import {
  InputSearchInputEventDetail,
  PIcon,
  PInputSearch,
  PMultiSelect,
  PMultiSelectOption,
  PMultiSelectProps,
  POptgroup,
  SelectToggleEventDetail,
} from '@porsche-design-system/components-react';
import { useId, useMemo, useState } from 'react';

import { Skeleton } from '../Skeleton';
import { useDebounce } from './useDebounce';

export type MultiAsyncSelectData = {
  options?: { label: string; value: string; appendix?: string }[];
  optgroups?: {
    label: string;
    options?: { label: string; value: string; appendix?: string }[];
  }[];
  selectedOptions?: { label: string; value: string }[];
  isLoading: boolean;
  isPending: boolean;
  error?: unknown;
};

export type MultiAsyncSelectState = ReturnType<
  typeof useMultiAsyncSelectState
>['selectState'];

/**
 * Owns the search/open state of a multi async select. Call it together with
 * your data hook and pass the results to `MultiAsyncSelectView`.
 */
export const useMultiAsyncSelectState = () => {
  const [searchValue, setSearchValue] = useState('');

  const [hasOpened, setHasOpened] = useState(false);

  const [debouncedSearchValue, isDebouncing] = useDebounce(searchValue, 600);

  return {
    enabled: hasOpened,
    searchString: debouncedSearchValue,
    selectState: { searchValue, setSearchValue, setHasOpened, isDebouncing },
  };
};

export type MultiAsyncSelectViewProps = Omit<PMultiSelectProps, 'onToggle'> & {
  i18n: {
    noData: string;
    error: string;
  };
  data: MultiAsyncSelectData;
  selectState: MultiAsyncSelectState;
  disabledValues?: string[];
};

export const MultiAsyncSelectView = ({
  i18n,
  data: { options, optgroups, selectedOptions, isPending, isLoading, error },
  selectState: { searchValue, setSearchValue, setHasOpened, isDebouncing },
  ...props
}: MultiAsyncSelectViewProps) => {
  const isSearchLoading = Boolean(isDebouncing || isLoading || isPending);

  const hasData = Boolean(
    (options && options.length > 0) ||
    (optgroups &&
      optgroups.some((group) => group.options && group.options.length > 0)),
  );

  const shouldShowSkeleton =
    !hasData && (isPending || isLoading) && !error && !searchValue;

  const id = useId();

  const onInput = (e: CustomEvent<InputSearchInputEventDetail>) => {
    const term = (e.target as HTMLElement & { value: string }).value;
    setSearchValue(term);
  };

  const onToggle = (e: CustomEvent<SelectToggleEventDetail>) => {
    if (e.detail.open) {
      setHasOpened(true);
    } else {
      setSearchValue('');
    }
  };

  return (
    <PMultiSelect {...props} onToggle={onToggle}>
      <PInputSearch
        slot="filter"
        name="search"
        value={searchValue}
        loading={isSearchLoading}
        clear
        indicator
        compact
        autoComplete="off"
        onInput={onInput}
        // Prevent bubbling
        onBlur={(e) => e.stopPropagation()}
        onChange={(e) => e.stopPropagation()}
      />

      <SelectedValue slot="selected">
        {selectedOptions?.length ? (
          <SelectedOption>
            {selectedOptions.map((opt) => opt.label).join(', ')}
          </SelectedOption>
        ) : null}
      </SelectedValue>

      {/* Initial skeleton loading */}
      {shouldShowSkeleton &&
        Array.from({ length: 10 }).map((_, i) => (
          <Skeleton
            data-testid="async-select-skeleton"
            key={`${id}-${i}`}
            css={{
              height: '30px',
              margin: '5px 10px',
            }}
          />
        ))}

      {/* Options */}
      {optgroups
        ? optgroups.map(({ label, options: groupOptions }) => (
            <POptgroup key={`${id}-${label}`} label={label}>
              {groupOptions?.map((opt) => (
                <PMultiSelectOption
                  key={opt.value}
                  value={opt.value}
                  disabled={props.disabledValues?.includes(opt.value)}
                >
                  {opt.appendix ? (
                    <OptionColumns>
                      {opt.label}
                      <OptionAppendix>{opt.appendix}</OptionAppendix>
                    </OptionColumns>
                  ) : (
                    opt.label
                  )}
                </PMultiSelectOption>
              ))}
            </POptgroup>
          ))
        : options?.map((opt) => (
            <PMultiSelectOption
              key={`${id}-${opt.value}`}
              value={opt.value}
              disabled={props.disabledValues?.includes(opt.value)}
            >
              {opt.appendix ? (
                <OptionColumns>
                  {opt.label}
                  <OptionAppendix>({opt.appendix})</OptionAppendix>
                </OptionColumns>
              ) : (
                opt.label
              )}
            </PMultiSelectOption>
          ))}

      {/* No filter results */}
      {!isSearchLoading && !hasData && !error && (
        <OptionStatus slot="options-status" role="alert">
          {i18n.noData}
        </OptionStatus>
      )}

      {/* Error state */}
      {error ? (
        <OptionStatus slot="options-status" role="alert">
          <PIcon name="information" color="error" />
          <span>{error instanceof Error ? error.message : i18n.error}</span>
        </OptionStatus>
      ) : null}
    </PMultiSelect>
  );
};

/**
 * This hook is used to transform async data into options and optgroups
 * for the MultiAsyncSelectView component. It also ensures that the currently selected
 * value is included in the options, even if it's not part of the
 * fetched data (e.g. due to filtering or pagination).
 */
export const useMultiAsyncData = <TData,>({
  data,
  selected,
  searchString,
  toOptionFn,
  equalsFn,
  groupByFn,
  groupOrder,
  appendixFn,
}: {
  data?: TData[] | null;
  selected?: TData[] | null;
  searchString?: string;
  toOptionFn: (d: TData) => { label: string; value: string };
  equalsFn?: (a: TData, b: TData) => boolean;
  groupByFn?: (d: TData) => string;
  groupOrder?: string[];
  appendixFn?: (d: TData) => string;
}) => {
  const { options, optgroups } = useMemo(() => {
    if (!data && !selected?.length) {
      return {
        options: [],
        optgroups: undefined,
      };
    }

    const missingSelected =
      selected && !searchString
        ? selected.filter(
            (s) => !data?.some((p) => (equalsFn ? equalsFn(p, s) : p === s)),
          )
        : [];

    const dataOptions = [...missingSelected, ...(data ?? [])];

    if (!groupByFn) {
      return {
        options: dataOptions.map((p) => ({
          ...toOptionFn(p),
          appendix: appendixFn?.(p),
        })),
        optgroups: undefined,
      };
    }

    const groups: Record<string, typeof dataOptions> = {};

    dataOptions.forEach((opt) => {
      const group = groupByFn(opt);
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(opt);
    });

    return {
      options: dataOptions.map((p) => ({
        ...toOptionFn(p),
        appendix: appendixFn?.(p),
      })),
      optgroups: Object.entries(groups)
        .toSorted(([labelA], [labelB]) => {
          const indexA = groupOrder?.indexOf(labelA) ?? -1;
          const indexB = groupOrder?.indexOf(labelB) ?? -1;
          if (indexA === -1 && indexB === -1) {
            return 0;
          }
          if (indexA === -1) {
            return 1;
          }
          if (indexB === -1) {
            return -1;
          }
          return indexA - indexB;
        })
        .map(([label, opts]) => ({
          label,
          options: opts.map((p) => ({
            ...toOptionFn(p),
            appendix: appendixFn?.(p),
          })),
        })),
    };
  }, [
    data,
    selected,
    searchString,
    groupOrder,
    toOptionFn,
    equalsFn,
    groupByFn,
    appendixFn,
  ]);

  return {
    options,
    optgroups,
  };
};

const OptionStatus = styled('div', {
  base: {
    display: 'flex',
    gap: 'sm',
    cursor: 'not-allowed',
    color: 'text.contrastMedium',
    padding: 'sm',
  },
});

const OptionColumns = styled('div', {
  base: {
    display: 'grid',
    gap: 'md',
    gridTemplateColumns: '[minmax(50%, 1fr) auto]',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    width: '100%',
    wordBreak: 'break-word',
  },
});

const OptionAppendix = styled('span', {
  base: {
    color: 'text.contrastMedium',
    textAlign: 'right',
  },
});

const SelectedValue = styled('span', {
  base: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
});

const SelectedOption = styled('span', {
  base: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});
